// @greenlight-spec: 16-api-conventions.md
// @greenlight-spec: 17-code-organization.md
import { initContract } from '@ts-rest/core';
import { AppFleetGraphResultSchema, AppListItemSchema, AppOwnerContactSchema, AppSchema, AppSummarySchema, AppsListQuerySchema, AppLifecycleStateSchema, AppUserPrefsSchema, CreateAppSchema, SetAppLifecycleExemptionSchema, GetAppResultSchema, UpdateAppSchema, paginated, } from '@greenlight/shared';
import { AppIdParamsSchema, AppSlugParamsSchema, errorResponses } from './common.js';
const c = initContract();
export const appsContract = c.router({
    list: {
        method: 'GET',
        path: '/api/apps',
        query: AppsListQuerySchema,
        responses: {
            200: paginated(AppListItemSchema),
            ...errorResponses,
        },
        summary: 'List apps in the caller’s org (cursor-paginated by last_deploy_at desc, never-deployed last). Favorites sort above the rest and hidden apps are excluded by default (T-7557); pass include_hidden=true to include them.',
    },
    // Keyed by slug, because the refused viewer only has the slug the access-denied
    // redirect carried; they have no app id and cannot read the list to find one.
    // Registered above the `/api/apps/:id` reads so `owner-contact` is not parsed as
    // an app id. Any signed-in member of the org may call it (docs/28): the page it
    // feeds already tells the viewer the app exists, and the point is to name the
    // person who can let them in.
    ownerContact: {
        method: 'GET',
        path: '/api/apps/by-slug/:slug/owner-contact',
        pathParams: AppSlugParamsSchema,
        responses: {
            200: AppOwnerContactSchema,
            ...errorResponses,
        },
        summary: 'Name an app and its owner for a viewer who cannot open it — app name, owner display name, owner email, and nothing else about the app.',
    },
    summary: {
        method: 'GET',
        path: '/api/apps/summary',
        responses: {
            200: AppSummarySchema,
            ...errorResponses,
        },
        summary: 'Org-wide app counts grouped by lifecycle status (apps-inventory summary).',
    },
    create: {
        method: 'POST',
        path: '/api/apps',
        body: CreateAppSchema,
        responses: {
            201: AppSchema,
            ...errorResponses,
        },
        summary: 'Register a new app (claims the slug under an advisory lock).',
    },
    fleetGraph: {
        method: 'GET',
        path: '/api/apps/graph',
        responses: {
            200: AppFleetGraphResultSchema,
            ...errorResponses,
        },
        summary: 'Fleet-wide topology for the master system map: every live app with its workloads, resources, integration grants, and whether its access is restricted (IT-only).',
    },
    get: {
        method: 'GET',
        path: '/api/apps/:id',
        pathParams: AppIdParamsSchema,
        responses: {
            200: GetAppResultSchema,
            ...errorResponses,
        },
        summary: 'Fetch a single app with its shared-environment summary and desired-state handles.',
    },
    update: {
        method: 'PATCH',
        path: '/api/apps/:id',
        pathParams: AppIdParamsSchema,
        body: UpdateAppSchema,
        responses: {
            200: AppSchema,
            ...errorResponses,
        },
        summary: 'Patch app fields (rename) or drive the suspend/restore kill switch.',
    },
    delete: {
        method: 'DELETE',
        path: '/api/apps/:id',
        pathParams: AppIdParamsSchema,
        body: c.noBody(),
        responses: {
            204: c.noBody(),
            ...errorResponses,
        },
        summary: 'Delete an app (suspends it, then tears down its resources).',
    },
    setFavorite: {
        method: 'PUT',
        path: '/api/apps/:id/favorite',
        pathParams: AppIdParamsSchema,
        body: c.noBody(),
        responses: {
            200: AppUserPrefsSchema,
            ...errorResponses,
        },
        summary: "Favorite an app on the caller's own apps list (personal view state, T-7557) — callable by any user who can see the app.",
    },
    unsetFavorite: {
        method: 'DELETE',
        path: '/api/apps/:id/favorite',
        pathParams: AppIdParamsSchema,
        body: c.noBody(),
        responses: {
            200: AppUserPrefsSchema,
            ...errorResponses,
        },
        summary: "Un-favorite an app on the caller's own apps list.",
    },
    setHidden: {
        method: 'PUT',
        path: '/api/apps/:id/hidden',
        pathParams: AppIdParamsSchema,
        body: c.noBody(),
        responses: {
            200: AppUserPrefsSchema,
            ...errorResponses,
        },
        summary: "Hide an app from the caller's own default apps list (personal view state, T-7557) — recoverable via include_hidden.",
    },
    unsetHidden: {
        method: 'DELETE',
        path: '/api/apps/:id/hidden',
        pathParams: AppIdParamsSchema,
        body: c.noBody(),
        responses: {
            200: AppUserPrefsSchema,
            ...errorResponses,
        },
        summary: "Un-hide an app on the caller's own apps list.",
    },
    // Inactivity lifecycle actions (docs/11 §Zombie App Detection). Confirming an
    // app is still needed is the owner's answer to the notice; the exemption is
    // IT's standing decision about an app that is idle by design.
    lifecycleExtend: {
        method: 'POST',
        path: '/api/apps/:id/lifecycle/extend',
        pathParams: AppIdParamsSchema,
        body: c.noBody(),
        responses: {
            200: AppLifecycleStateSchema,
            ...errorResponses,
        },
        summary: 'Confirm an app is still needed: resets its inactivity clock to now and clears any cleanup flag, from whatever state it was in.',
    },
    lifecycleExemption: {
        method: 'PUT',
        path: '/api/apps/:id/lifecycle/exemption',
        pathParams: AppIdParamsSchema,
        body: SetAppLifecycleExemptionSchema,
        responses: {
            200: AppLifecycleStateSchema,
            ...errorResponses,
        },
        summary: 'Exempt an app from the inactivity lifecycle, or clear the exemption (IT). An exempt app is never classified, notified, or paused for being idle.',
    },
});
//# sourceMappingURL=apps.js.map